import { useEffect, useMemo, useState } from 'react';
import { FONT_MONO, SQRT3, hexPoints, buildHexCells, linesForCells, sizeFromCellCount, v } from '../constants';
import type { Axis } from '../constants';
import type { Cell, EvaluatedConstraint, Restriction } from '../types';
import { applyModifier, computeLineStats } from '../rules';

// ─────────────────────────────────────────────────────────────────────
// Visual primitives (SVG) and the interactive hex board
// ─────────────────────────────────────────────────────────────────────

interface HexCellBgProps {
  cx: number; cy: number; R: number;
  kind: 'empty' | 'restricted' | 'modifier' | 'invisible';
  restriction?: Restriction;
}
export function HexCellBg({ cx, cy, R, kind, restriction }: HexCellBgProps) {
  let fill = v('cellEmpty');
  let stroke = v('cellEmptyStroke');
  let dash = '';
  let strokeW = 1.25;

  if (kind === 'restricted') {
    const tintMap: Record<Restriction, [string, string]> = {
      even:  ['rEven',  'rEvenInk'],
      odd:   ['rOdd',   'rOddInk'],
      pos:   ['rPos',   'rPosInk'],
      neg:   ['rNeg',   'rNegInk'],
      prime: ['rPrime', 'rPrimeInk'],
      magHi: ['rMagHi', 'rMagHiInk'],
      magLo: ['rMagLo', 'rMagLoInk'],
    };
    const [t, ink] = (restriction && tintMap[restriction]) || ['cellEmpty', 'inkSoft'];
    fill = v(t);
    stroke = v(ink);
    dash = '3 3';
  } else if (kind === 'modifier') {
    fill = v('cellEmpty');
    stroke = v('ink');
    strokeW = 1.5;
  }

  return (
    <polygon
      points={hexPoints(cx, cy, R)}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeW}
      strokeDasharray={dash}
      strokeLinejoin="round"
    />
  );
}

export function HexRestrictionGlyph({ cx, cy, R, kind, ink }: { cx: number; cy: number; R: number; kind: Restriction; ink?: string }) {
  const fs = R * 0.5;
  const map: Record<Restriction, string> = {
    even:  'Even',
    odd:   'Odd',
    pos:   '+',
    neg:   '−',
    prime: 'p',
    magHi: '|x|≥5',
    magLo: '|x|≤4',
  };
  const inkColor = ink ? v(ink) : v('inkSoft');
  const wideKinds: Restriction[] = ['magHi', 'magLo', 'even', 'odd'];
  return (
    <text
      x={cx} y={cy}
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily={FONT_MONO}
      fontWeight={500}
      fontSize={wideKinds.includes(kind) ? R * 0.36 : fs}
      fill={inkColor}
      opacity={0.85}
      style={{ pointerEvents: 'none' }}
    >{map[kind]}</text>
  );
}

function restrictionInk(restriction: Restriction | undefined): string {
  const map: Record<Restriction, string> = {
    even: 'rEvenInk', odd: 'rOddInk', pos: 'rPosInk', neg: 'rNegInk',
    prime: 'rPrimeInk', magHi: 'rMagHiInk', magLo: 'rMagLoInk',
  };
  return (restriction && map[restriction]) || 'inkSoft';
}

// Empty-cell modifier display: a small placeholder hex (containing "#") next
// to the modifier expression, so the cell reads as "<tile> + 5" — making it
// obvious that the modifier transforms the tile placed here.
//
// Exponents (² / ³) render as proper superscripts at the upper-right of the
// hex instead of inline, which would look like a subscript.
export function HexModifierBadge({ cx, cy, R, label: rawLabel }: { cx: number; cy: number; R: number; label: string }) {
  // The puzzle data uses `×` for multiplication; we render it as `*` on
  // the modifier badge per the player-facing convention (math chips still
  // show `×`). Negation `×-` displays as `*-`. Exponents are unaffected.
  const label = rawLabel.startsWith('×') ? '*' + rawLabel.slice(1) : rawLabel;
  const isExponent = label === '²' || label === '³';

  // Cross-browser SVG vertical centering: the alphabetic baseline + dy="0.35em"
  // pattern renders identically in Chrome, Safari, and Firefox, unlike
  // dominantBaseline="central" which Safari interprets differently.
  const VCENTER_DY = '0.35em';

  if (isExponent) {
    const placeR = R * 0.34;
    const expFontSize = R * 0.5;
    const accent = v('accent');
    return (
      <g style={{ pointerEvents: 'none' }} opacity={0.9}>
        <polygon
          points={hexPoints(cx, cy, placeR)}
          fill="none"
          stroke={accent}
          strokeWidth={1}
          strokeDasharray="2 2"
          strokeLinejoin="round"
        />
        <text
          x={cx} y={cy}
          dy={VCENTER_DY}
          textAnchor="middle"
          fontFamily={FONT_MONO}
          fontWeight={500}
          fontSize={placeR * 1.05}
          fill={accent}
        >#</text>
        <text
          x={cx + placeR * 1.05}
          y={cy - placeR * 0.85}
          dy={VCENTER_DY}
          textAnchor="start"
          fontFamily={FONT_MONO}
          fontWeight={600}
          fontSize={expFontSize}
          fill={accent}
          style={{ letterSpacing: '-0.02em' }}
        >{label}</text>
      </g>
    );
  }

  const m = label.match(/^([*+\-−÷])(.+)$/);
  const sign = m ? m[1] : null;
  const rest = m ? m[2] : null;

  const placeR = R * 0.32;
  const fontSize = R * 0.5;
  const charW = fontSize * 0.62;
  const gap = R * 0.18;
  // Half-character gap between operator and operand for visual breathing room
  // without the full-space drift the literal " " produced.
  const innerGap = charW * 0.5;
  const visualLen = sign ? (1 + rest!.length) : label.length;
  const textW = visualLen * charW + (sign ? innerGap : 0);
  const totalW = placeR * 2 + gap + textW;
  const startX = cx - totalW / 2;
  const placeCx = startX + placeR;
  const textX = startX + placeR * 2 + gap;
  const accent = v('accent');
  return (
    <g style={{ pointerEvents: 'none' }} opacity={0.9}>
      <polygon
        points={hexPoints(placeCx, cy, placeR)}
        fill="none"
        stroke={accent}
        strokeWidth={1}
        strokeDasharray="2 2"
        strokeLinejoin="round"
      />
      <text
        x={placeCx} y={cy}
        dy={VCENTER_DY}
        textAnchor="middle"
        fontFamily={FONT_MONO}
        fontWeight={500}
        fontSize={placeR * 1.05}
        fill={accent}
      >#</text>
      <text
        x={textX} y={cy}
        dy={VCENTER_DY}
        textAnchor="start"
        fontFamily={FONT_MONO}
        fontWeight={600}
        fontSize={fontSize}
        fill={accent}
        style={{ letterSpacing: '-0.02em' }}
      >
        {sign && rest ? (
          <>
            <tspan>{sign}</tspan>
            <tspan dx={innerGap}>{rest}</tspan>
          </>
        ) : label}
      </text>
    </g>
  );
}

function HexModifierCorner({ cx, cy, R, label }: { cx: number; cy: number; R: number; label: string }) {
  const x = cx + R * 0.55;
  const y = cy - R * 0.65;
  return (
    <g style={{ pointerEvents: 'none' }}>
      <circle cx={x} cy={y} r={R * 0.28} fill={v('modBg')} stroke={v('modBg')} />
      <text
        x={x} y={y + 0.5}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={FONT_MONO}
        fontWeight={500}
        fontSize={R * 0.34}
        fill={v('modInk')}
      >{label}</text>
    </g>
  );
}

interface HexTileProps {
  cx: number; cy: number; R: number;
  value: number | undefined;
  locked?: boolean;
  modified?: number | null;
  showOriginal?: boolean;
  animateKey?: string;
}
export function HexTile({ cx, cy, R, value, locked = false, modified = null, showOriginal = false, animateKey }: HexTileProps) {
  // Locked tiles render at the full cell radius so they match the surrounding
  // empty/restriction/modifier cells in size; placed tiles keep a small inset
  // for the "card on a cell" placed look.
  const innerR = locked ? R : R - 4;
  const fill   = locked ? v('tileLockedFill')   : v('tileFill');
  const stroke = locked ? v('tileLockedStroke') : v('tileStroke');
  const ink    = locked ? v('tileLockedInk')    : v('ink');
  const display = modified != null ? modified : value;
  const fontSize = R * 0.85;
  return (
    <g
      key={animateKey}
      className={`hm-placed-tile-svg ${animateKey ? 'entering' : ''}`}
      style={{ pointerEvents: 'none' }}
    >
      <polygon
        points={hexPoints(cx, cy, innerR)}
        fill={fill}
        stroke={stroke}
        strokeWidth={locked ? 1.25 : 1.5}
        strokeLinejoin="round"
      />
      {!locked && (
        <polygon
          points={hexPoints(cx, cy - 0.5, innerR - 1.5)}
          fill="none"
          stroke={v('ruleSoft')}
          strokeWidth={0.75}
          strokeLinejoin="round"
        />
      )}
      <text
        x={cx} y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={FONT_MONO}
        fontWeight={500}
        fontSize={fontSize}
        fill={ink}
        style={{ letterSpacing: '-0.02em' }}
      >{display}</text>
      {showOriginal && modified != null && (
        <text
          x={cx - innerR * 0.55}
          y={cy + innerR * 0.55}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily={FONT_MONO}
          fontWeight={500}
          fontSize={fontSize * 0.42}
          fill={v('inkMute')}
        >{value}</text>
      )}
      {locked && (
        <g transform={`translate(${cx + innerR * 0.5}, ${cy - innerR * 0.55})`}>
          <rect x={-3.5} y={-2} width={7} height={6} rx={1} fill={ink} opacity={0.7}/>
          <path d="M -2,-2 v -1.5 a 2,2 0 0 1 4,0 v 1.5" stroke={ink} strokeWidth={1} fill="none" opacity={0.7}/>
        </g>
      )}
    </g>
  );
}

interface ConstraintChipProps {
  x: number; y: number;
  type: string;
  value?: number | number[];
  status?: 'ok' | 'bad' | 'neutral';
  anchor?: 'start' | 'middle' | 'end';
  axis: Axis; index: number;
  active?: boolean;
  pinned?: boolean;
  current?: number;
  total?: number;
  onHover?: (over: { axis: Axis; index: number } | null) => void;
  onClick?: (over: { axis: Axis; index: number }) => void;
}
function ConstraintChip({ x, y, type, value, status = 'neutral', anchor = 'start', axis, index, active = false, pinned = false, current, total, onHover, onClick }: ConstraintChipProps) {
  let border = v('rule');
  let fg = v('inkSoft');
  let fill = v('paper');
  if (status === 'ok')  { border = v('ok');  fg = v('ok');  fill = v('chipOkFill'); }
  else if (status === 'bad') { border = v('bad'); fg = v('bad'); fill = v('chipBadFill'); }
  // Active = hovered, pinned, OR on a line through the focused cell. We
  // tint the border accent so the affected chips read as "linked" to
  // the focused cell without competing with the stronger pinned state.
  if (pinned) { border = v('accent'); }
  else if (active) { border = v('accent'); }

  // Type-specific label. Math-symbol chips (Σ, ×, ≥, ≤) render `glyph N`
  // with a live `current/target` when active. Word chips (Even, Odd,
  // Positive, All Negative) read as plain English. negCount embeds its
  // count in the word ("2 Negatives") with a live `current/target` prefix
  // when active.
  let label: string;
  if (type === 'sum' || type === 'product' || type === 'sumGte' || type === 'sumLte') {
    const glyph = type === 'sum' ? 'Σ' : type === 'product' ? '×' : type === 'sumGte' ? '≥' : '≤';
    const showCurrent = active && current != null;
    label = showCurrent ? `${glyph} ${current}/${value}` : `${glyph} ${value}`;
  } else if (type === 'allEven') {
    label = 'Even';
  } else if (type === 'allOdd') {
    label = 'Odd';
  } else if (type === 'noNeg') {
    label = 'Pos.';
  } else if (type === 'allNeg') {
    label = 'All Neg.';
  } else if (type === 'negCount') {
    const n = Number(value) || 0;
    const showCurrent = active && current != null;
    label = showCurrent ? `${current}/${n} Neg.` : `${n} Neg.`;
  } else if (type === 'contains') {
    label = `{${(value as number[] || []).join(',')}}`;
  } else {
    label = '';
  }

  const padX = 8;
  const fontSize = 12;
  const charW = 7.5;
  const textW = label.length * charW;
  const h = 22;
  const w = Math.max(28, textW + 2 * padX);
  let dx = 0;
  if (anchor === 'end') dx = -w;
  else if (anchor === 'middle') dx = -w / 2;

  const handleEnter = () => onHover && onHover({ axis, index });
  const handleLeave = () => onHover && onHover(null);
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick && onClick({ axis, index });
  };

  return (
    <g transform={`translate(${x},${y})`}>
      <g transform={`translate(${dx},${-h / 2})`}>
        <rect
          x={0} y={0} width={w} height={h} rx={h / 2}
          fill={fill}
          stroke={border}
          strokeWidth={pinned ? 2 : active ? 1.75 : 1.25}
          className="hm-chip-hit"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onClick={handleClick}
        />
        <text
          x={w / 2}
          y={h / 2 + 0.5}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily={FONT_MONO}
          fontWeight={500}
          fontSize={fontSize}
          fill={fg}
          style={{ letterSpacing: '-0.01em', pointerEvents: 'none' }}
        >{label}</text>
      </g>
    </g>
  );
}

// Bag tile (the hex chip shown in the bag below the board).
// `dim` covers two cases: tile is already placed (faded out forever)
// OR tile is invalid in the current focus/pin context (faded only
// while focus is active). The Bag component is responsible for
// telling us which; we just render the requested opacity.
//
// The `highlighted` prop is no longer used — the accent-outline
// approach was replaced with a "dim the invalid ones" treatment in
// 2026-05-13. Kept on the prop type for backward compat with any
// stray caller; ignored by the renderer.
export function BagTile({ value, dim = false, selected = false, highlighted: _highlighted = false, size = 44 }: { value: number; dim?: boolean; selected?: boolean; highlighted?: boolean; size?: number }) {
  void _highlighted;
  const R = size / 2;
  const padR = R + 2;
  const w = 2 * padR;
  const h = SQRT3 * padR;
  const fill   = selected ? v('tileSelectedFill')   : v('tileFill');
  const stroke = selected ? v('tileSelectedStroke') : v('tileStroke');
  const ink    = selected ? v('tileSelectedInk')    : v('ink');
  return (
    <svg
      width={2 * R}
      height={SQRT3 * R}
      viewBox={`${-padR} ${-h / 2} ${w} ${h}`}
      className={`hm-bag-tile-svg ${dim ? 'placed' : ''}`}
      style={{ overflow: 'visible' }}
    >
      <polygon
        points={hexPoints(0, 0, R - 1)}
        fill={fill}
        stroke={stroke}
        strokeWidth={selected ? 2 : 1.5}
        strokeLinejoin="round"
      />
      <text
        x={0} y={0}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={FONT_MONO}
        fontWeight={500}
        fontSize={R * 0.85}
        fill={ink}
        style={{ letterSpacing: '-0.02em' }}
      >{value}</text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Hex board — interactive
// ─────────────────────────────────────────────────────────────────────
function lineEndpoints(axis: Axis, lineIdx: number, geom: { x: number; y: number }[], lines: Record<Axis, number[][]>): { start: { x: number; y: number }; end: { x: number; y: number } } {
  const cellIdxs = lines[axis][lineIdx];
  const cs = cellIdxs.map(i => geom[i]);
  if (axis === 'col') {
    const top    = cs.reduce((a, b) => a.y < b.y ? a : b);
    const bottom = cs.reduce((a, b) => a.y > b.y ? a : b);
    return { start: top, end: bottom };
  }
  if (axis === 'nwse') {
    const start = cs.reduce((a, b) => a.x < b.x ? a : b);
    const end   = cs.reduce((a, b) => a.x > b.x ? a : b);
    return { start, end };
  }
  // nesw — chip on the SW (lower-left) end
  const start = cs.reduce((a, b) => a.x > b.x ? a : b);
  const end   = cs.reduce((a, b) => a.x < b.x ? a : b);
  return { start, end };
}

function chipPlacement(axis: Axis, lineIdx: number, geom: { x: number; y: number }[], R: number, lines: Record<Axis, number[][]>): { x: number; y: number; anchor: 'start' | 'middle' | 'end' } {
  const { start, end } = lineEndpoints(axis, lineIdx, geom, lines);
  if (axis === 'col') {
    return { x: start.x, y: start.y - R - 18, anchor: 'middle' };
  }
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / len, uy = dy / len;
  const offset = R + 22;
  return {
    x: end.x + ux * offset,
    y: end.y + uy * offset,
    anchor: ux >= 0 ? 'start' : 'end',
  };
}

interface DragState {
  type: 'bag' | 'cell';
  tileId?: string;
  cellIdx?: number;
  value: number | undefined;
  rawValue?: number | undefined;
  startX: number; startY: number;
  x: number; y: number;
  active: boolean;
  overIdx: number | null;
  overBag?: boolean;
}

export interface ActiveConstraint {
  axis: Axis; index: number;
  type: string;
  value?: number | number[];
}

interface HexBoardProps {
  cells: Cell[];
  evaluatedConstraints: EvaluatedConstraint[];
  badRestr: Set<number>;
  R?: number;
  gap?: number;
  pad?: number;
  onCellClick: (i: number) => void;
  onCellPointerDown: (i: number, e: React.PointerEvent) => void;
  clickableCells: Set<number>;
  draggableCells: Set<number>;
  recentlyPlaced: number | null;
  drag: DragState | null;
  dragOverIdx: number | null;
  onActiveConstraintChange?: (c: ActiveConstraint | null) => void;
  winEpicenter?: number | null;
  /// Cell index to pulse with a hint glow. `null` when no hint is active.
  hintCellIdx?: number | null;
  /// 'place' = accent ring (drag a tile here); 'undo' = warning ring
  /// (this placed tile is wrong).
  hintKind?: 'place' | 'undo' | null;
  /// Force a constraint chip into pinned state from outside the board —
  /// the tutorial uses this to teach a specific chip on each step.
  /// Takes precedence over hover and the internal pin state.
  forcedPinnedLine?: { axis: Axis; index: number } | null;
  /// When set, marks that cell as "focused" and highlights every line
  /// passing through it (plus every constraint chip on those lines).
  /// Wired by the player clicking a cell with no tile selected.
  focusedCellIdx?: number | null;
  /// Editor mode — when true, every line gets either a chip (if a
  /// constraint exists) or a faint "+" placeholder at the same
  /// endpoint. Clicking ANY chip (real or placeholder) fires
  /// `onSlotClick` instead of the default pin-toggle. Used by the
  /// puzzle designer to make constraints clickable in-board.
  editorSlots?: boolean;
  onSlotClick?: (axis: Axis, index: number) => void;
}

export function HexBoard({
  cells, evaluatedConstraints, badRestr,
  R = 38, gap = 5, pad = 100,
  onCellClick, onCellPointerDown, clickableCells, draggableCells,
  recentlyPlaced,
  drag, dragOverIdx,
  onActiveConstraintChange,
  winEpicenter = null,
  hintCellIdx = null,
  hintKind = null,
  forcedPinnedLine = null,
  focusedCellIdx = null,
  editorSlots = false,
  onSlotClick,
}: HexBoardProps) {
  const size = sizeFromCellCount(cells.length);
  const lines = useMemo(() => linesForCells(cells.length), [cells.length]);
  const geom = useMemo(() => buildHexCells(R, gap, size), [R, gap, size]);
  const xs = geom.map(c => c.x);
  const ys = geom.map(c => c.y);
  const minX = Math.min(...xs) - R - pad;
  const maxX = Math.max(...xs) + R + pad;
  const minY = Math.min(...ys) - R - pad;
  const maxY = Math.max(...ys) + R + pad;
  const w = maxX - minX;
  const h = maxY - minY;

  const [hoveredLine, setHoveredLine] = useState<{ axis: Axis; index: number } | null>(null);
  const [pinnedLine, setPinnedLine] = useState<{ axis: Axis; index: number } | null>(null);
  // External (tutorial) override beats hover and internal pin — when the
  // tutorial says "look at this chip", we want it to stay highlighted.
  // Hover wins over the internal pin so the user can preview other
  // constraints while one is locked. Pin re-asserts itself when nothing's
  // hovered.
  const effectivePinned = forcedPinnedLine ?? pinnedLine;
  const activeLine = forcedPinnedLine ?? hoveredLine ?? pinnedLine;
  // Cells on the active (hover/pin) line, kept separate from
  // focus-cell highlighting so the two interaction modes don't
  // stomp each other.
  const activeLineCells = useMemo(() => {
    if (!activeLine) return new Set<number>();
    return new Set(lines[activeLine.axis][activeLine.index]);
  }, [activeLine, lines]);

  // Lines passing through the focused cell, keyed `<axis>:<index>`.
  // Drives chip-affected styling + the merged cell highlight set.
  // Set of (axis:index) keys for lines that actually have a constraint
  // chip on them. Drives the "only highlight constrained lines" filter
  // below — unconstrained lines don't matter to solving the puzzle, so
  // calling them out is just visual noise.
  const constrainedLineKeys = useMemo(() => {
    const set = new Set<string>();
    evaluatedConstraints.forEach(c => set.add(`${c.axis}:${c.index}`));
    return set;
  }, [evaluatedConstraints]);

  const focusedLineKeys = useMemo(() => {
    if (focusedCellIdx == null) return new Set<string>();
    const set = new Set<string>();
    (['col', 'nwse', 'nesw'] as Axis[]).forEach(axis => {
      lines[axis].forEach((cellIdxs, idx) => {
        const key = `${axis}:${idx}`;
        if (!constrainedLineKeys.has(key)) return;
        if (cellIdxs.includes(focusedCellIdx)) set.add(key);
      });
    });
    return set;
  }, [focusedCellIdx, lines, constrainedLineKeys]);

  // Union of cells on CONSTRAINED lines that pass through the focused
  // cell. Skips unconstrained lines per the user's note 2026-05-13:
  // "no need to highlight cells that don't contain constraints because
  // they don't matter to solve the puzzle." Rendered with the same
  // `highlighted` visual as the active-line pinning — the focused
  // cell itself gets an extra distinct ring.
  const focusedHighlightCells = useMemo(() => {
    if (focusedCellIdx == null) return new Set<number>();
    const set = new Set<number>();
    (['col', 'nwse', 'nesw'] as Axis[]).forEach(axis => {
      lines[axis].forEach((cellIdxs, idx) => {
        if (!constrainedLineKeys.has(`${axis}:${idx}`)) return;
        if (cellIdxs.includes(focusedCellIdx)) cellIdxs.forEach(c => set.add(c));
      });
    });
    // Always include the focused cell itself so its ring renders even
    // when it sits on no constrained lines (rare on real puzzles).
    set.add(focusedCellIdx);
    return set;
  }, [focusedCellIdx, lines, constrainedLineKeys]);

  const highlightedCells = useMemo(() => {
    const s = new Set<number>();
    activeLineCells.forEach(c => s.add(c));
    focusedHighlightCells.forEach(c => s.add(c));
    return s;
  }, [activeLineCells, focusedHighlightCells]);

  // Live "current value" per constraint, used by chip when active.
  const liveStats = useMemo(() => {
    return evaluatedConstraints.map(c => computeLineStats(cells, lines[c.axis][c.index]));
  }, [cells, evaluatedConstraints, lines]);

  // Notify parent of the currently-active constraint (for bag-tile hinting).
  useEffect(() => {
    if (!onActiveConstraintChange) return;
    if (!activeLine) { onActiveConstraintChange(null); return; }
    const c = evaluatedConstraints.find(x => x.axis === activeLine.axis && x.index === activeLine.index);
    if (!c) { onActiveConstraintChange(null); return; }
    onActiveConstraintChange({ axis: c.axis, index: c.index, type: c.type, value: c.value });
  }, [activeLine, evaluatedConstraints, onActiveConstraintChange]);

  const togglePin = (line: { axis: Axis; index: number }) => {
    // Clear hover on click — on touch devices a tap fires `mouseenter`
    // before `click`, so without this the line stays highlighted via
    // hover even after the pin is toggled off, requiring a second tap
    // somewhere else to dismiss.
    setHoveredLine(null);
    setPinnedLine(p => (p && p.axis === line.axis && p.index === line.index) ? null : line);
  };

  // Zoom + pan removed 2026-05-12 — the affordance wasn't earning its
  // keep against the cost of overlapping the bag + the pan-vs-tile-drag
  // event-routing complexity. The board now renders at a single fixed
  // zoom level; the SVG keeps `overflow: hidden` so any stray paint
  // doesn't bleed into the bag below.

  // While dragging, empty cells are always valid drop targets (place there).
  // Cell-to-cell drags additionally treat other placed cells as swap targets;
  // locked cells are never valid.
  const validDropCells = useMemo<Set<number> | null>(() => {
    if (!drag || !drag.active) return null;
    const set = new Set<number>();
    cells.forEach((c, i) => {
      if (c.kind === 'empty') {
        if (drag.type === 'cell' && drag.cellIdx === i) return;
        set.add(i);
      } else if (c.kind === 'placed' && drag.type === 'cell' && drag.cellIdx !== i) {
        set.add(i);
      }
    });
    return set;
  }, [drag, cells]);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`${minX} ${minY} ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block', overflow: 'visible' }}
      role="grid"
      aria-label="Hex Math board"
    >
      {geom.map((g, i) => {
        const cell = cells[i];
        // Gap cells are holes in the grid — small dashed marker so the
        // layout intent reads at a glance. In play mode the gap is
        // non-interactive (aria-hidden, no hit area), so the user
        // can't waste taps on a hole. In editor mode (gap is in
        // clickableCells) we ALSO mount an invisible full-hex hit
        // polygon with the data-cell-idx attribute so the designer
        // can drag/tap-stamp other kinds onto the slot — otherwise
        // once a cell becomes a gap it stays a gap forever.
        if (cell.kind === 'gap') {
          const isClickable = clickableCells.has(i);
          if (!isClickable) {
            return (
              <g key={i} aria-hidden="true">
                <polygon
                  points={hexPoints(g.x, g.y, R * 0.32)}
                  fill="none"
                  stroke={v('rule')}
                  strokeOpacity={0.35}
                  strokeWidth={0.75}
                  strokeDasharray="2 2"
                  strokeLinejoin="round"
                />
              </g>
            );
          }
          return (
            <g
              key={i}
              data-cell-idx={i}
              className="hm-hex-cell clickable"
              onClick={() => onCellClick(i)}
              role="button"
              aria-label={`Cell ${i+1} (gap)`}
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCellClick(i);
                }
              }}
            >
              {/* Visible marker, same as play mode. */}
              <polygon
                points={hexPoints(g.x, g.y, R * 0.32)}
                fill="none"
                stroke={v('rule')}
                strokeOpacity={0.35}
                strokeWidth={0.75}
                strokeDasharray="2 2"
                strokeLinejoin="round"
              />
              {/* Invisible hit area — full hex footprint so taps + the
                  drag elementFromPoint hit-test reach this cell even
                  though the visible marker is tiny. */}
              <polygon
                points={hexPoints(g.x, g.y, R)}
                fill="transparent"
                stroke="transparent"
              />
            </g>
          );
        }
        const isClickable = clickableCells.has(i);
        const isDraggable = draggableCells.has(i);
        const showRest = cell.kind === 'empty' && !!cell._restriction;
        const showMod  = cell.kind === 'empty' && !!cell.modifier;
        const isPlaced = cell.kind === 'placed';
        const isLocked = cell.kind === 'locked';
        const isDragSource = !!drag && drag.active && drag.type === 'cell' && drag.cellIdx === i;
        const isHighlighted = highlightedCells.has(i);
        const isValidDropTarget = !!validDropCells && validDropCells.has(i);
        const isHoveredDrop = isValidDropTarget && dragOverIdx === i;

        let bgKind: 'empty' | 'restricted' | 'modifier' | 'invisible' = 'empty';
        if (showRest) bgKind = 'restricted';
        else if (showMod) bgKind = 'modifier';
        else if (isLocked || isPlaced) bgKind = 'invisible';

        const handlePointerDown = (e: React.PointerEvent) => {
          if (onCellPointerDown && isDraggable) onCellPointerDown(i, e);
        };
        const handleClick = isClickable ? () => onCellClick(i) : undefined;

        return (
          <g
            key={i}
            data-cell-idx={i}
            className={`hm-hex-cell ${isClickable ? 'clickable' : ''} ${isDraggable ? 'draggable' : ''} ${isDragSource ? 'dragging' : ''}`}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            role={isClickable ? 'button' : undefined}
            aria-label={isClickable ? `Cell ${i+1}` : undefined}
            tabIndex={isClickable ? 0 : -1}
            onKeyDown={isClickable ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCellClick(i);
              }
            } : undefined}
          >
            {bgKind === 'invisible' ? (
              <polygon
                className="hm-hover-poly"
                points={hexPoints(g.x, g.y, R)}
                fill="transparent"
                stroke="transparent"
              />
            ) : (
              <HexCellBg
                cx={g.x} cy={g.y} R={R}
                kind={bgKind}
                restriction={cell._restriction}
              />
            )}

            {showRest && cell._restriction && (
              <HexRestrictionGlyph
                cx={g.x} cy={g.y} R={R}
                kind={cell._restriction}
                ink={restrictionInk(cell._restriction)}
              />
            )}
            {showMod && cell.modifier && (
              <HexModifierBadge cx={g.x} cy={g.y} R={R} label={cell.modifier} />
            )}

            {(isLocked || isPlaced) && (
              <HexTile
                cx={g.x} cy={g.y} R={R}
                value={cell.value}
                modified={cell.modifier != null && cell.value != null
                  ? (applyModifier(cell.value, cell.modifier) ?? null)
                  : null}
                showOriginal={cell.modifier != null && cell.value != null}
                locked={isLocked}
                animateKey={recentlyPlaced === i ? `p-${i}` : undefined}
              />
            )}
            {(isLocked || isPlaced) && cell.modifier && (
              <HexModifierCorner cx={g.x} cy={g.y} R={R} label={cell.modifier} />
            )}

            {isHighlighted && (
              <polygon
                points={hexPoints(g.x, g.y, R)}
                fill={v('accent')}
                fillOpacity={0.14}
                stroke={v('accent')}
                strokeWidth={2}
                strokeOpacity={0.85}
                strokeLinejoin="round"
                style={{ pointerEvents: 'none' }}
              />
            )}

            {hintCellIdx === i && (
              <polygon
                data-hint-cell
                points={hexPoints(g.x, g.y, R)}
                fill="none"
                stroke={v(hintKind === 'undo' ? 'bad' : 'accent')}
                strokeWidth={3}
                strokeLinejoin="round"
                // SVG default transform-origin is (0,0) of the SVG, which
                // makes scale() drift toward the upper-left. Anchor to the
                // polygon's own center so the pulse expands in place.
                style={{
                  pointerEvents: 'none',
                  animation: 'hm-hint-pulse 0.9s ease-out 3',
                  transformOrigin: `${g.x}px ${g.y}px`,
                  transformBox: 'view-box',
                }}
              />
            )}

            {isValidDropTarget && !isHoveredDrop && (
              <polygon
                points={hexPoints(g.x, g.y, R)}
                fill={v('ok')}
                fillOpacity={0.08}
                stroke={v('ok')}
                strokeWidth={1.25}
                strokeOpacity={0.55}
                strokeDasharray="3 3"
                strokeLinejoin="round"
                style={{ pointerEvents: 'none' }}
              />
            )}
            {isHoveredDrop && (
              <polygon
                points={hexPoints(g.x, g.y, R)}
                fill={v('ok')}
                fillOpacity={0.18}
                stroke={v('ok')}
                strokeWidth={2}
                strokeOpacity={0.95}
                strokeLinejoin="round"
                style={{ pointerEvents: 'none' }}
              />
            )}

            {focusedCellIdx === i && (
              <polygon
                points={hexPoints(g.x, g.y, R + 2)}
                fill="none"
                stroke={v('accent')}
                strokeWidth={2}
                strokeDasharray="4 3"
                strokeLinejoin="round"
                style={{ pointerEvents: 'none' }}
              />
            )}

            {badRestr.has(i) && (
              <polygon
                className="hm-cell-error-ring"
                points={hexPoints(g.x, g.y, R + 3)}
                strokeWidth={2}
              />
            )}
          </g>
        );
      })}

      {evaluatedConstraints.map((c, idx) => {
        const { x, y, anchor } = chipPlacement(c.axis, c.index, geom, R, lines);
        const onActiveLine = !!activeLine && activeLine.axis === c.axis && activeLine.index === c.index;
        const onFocusedLine = focusedLineKeys.has(`${c.axis}:${c.index}`);
        const isActive = onActiveLine || onFocusedLine;
        const isPinned = !!effectivePinned && effectivePinned.axis === c.axis && effectivePinned.index === c.index;
        const stats = liveStats[idx];
        let current: number | undefined;
        let total: number | undefined;
        if (c.type === 'sum' || c.type === 'sumGte' || c.type === 'sumLte') current = stats.sum;
        else if (c.type === 'product') current = stats.product;
        else if (c.type === 'negCount') current = stats.negCount;
        else if (c.type === 'noNeg') { current = stats.posCount; total = stats.lineLength; }
        return (
          <ConstraintChip
            key={`c-${c.axis}-${c.index}-${idx}`}
            x={x} y={y}
            type={c.type} value={c.value}
            status={c.status} anchor={anchor}
            axis={c.axis} index={c.index}
            active={isActive}
            pinned={isPinned}
            current={current}
            total={total}
            onHover={setHoveredLine}
            onClick={onSlotClick ? (over) => onSlotClick(over.axis, over.index) : togglePin}
          />
        );
      })}

      {/* Editor mode — render a faint "+" placeholder at every line
          endpoint that doesn't already carry a constraint. Tapping the
          placeholder opens the designer's constraint picker for that
          line. The placeholder hides on hover so it doesn't compete
          with a constraint chip ghost. */}
      {editorSlots && onSlotClick && (['col', 'nwse', 'nesw'] as Axis[]).flatMap(axis =>
        lines[axis].map((_, lineIdx) => {
          const exists = evaluatedConstraints.some(c => c.axis === axis && c.index === lineIdx);
          if (exists) return null;
          const { x, y } = chipPlacement(axis, lineIdx, geom, R, lines);
          const isActive = !!activeLine && activeLine.axis === axis && activeLine.index === lineIdx;
          return (
            <g
              key={`slot-${axis}-${lineIdx}`}
              transform={`translate(${x}, ${y})`}
              className="hm-chip-hit"
              onMouseEnter={() => setHoveredLine({ axis, index: lineIdx })}
              onMouseLeave={() => setHoveredLine(null)}
              onClick={() => onSlotClick(axis, lineIdx)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                r={11}
                fill={v('paperDeep')}
                stroke={isActive ? v('accent') : v('rule')}
                strokeWidth={isActive ? 1.5 : 1}
                strokeDasharray={isActive ? undefined : '3 3'}
              />
              <text
                x={0} y={0}
                dy="0.35em"
                textAnchor="middle"
                fontSize={14}
                fontWeight={600}
                fill={isActive ? v('accent') : v('inkMute')}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >+</text>
            </g>
          );
        }).filter(Boolean)
      )}

      {/* Win-celebration ripple — expanding accent circles propagating outward
          from the last placed cell, one per cell, delayed by distance.
          Each ripple is wrapped in a <g translate> so the circle stays at
          origin and CSS `transform: scale()` animates around its centre.
          The epicenter guard catches a Sentry-reported race: if the puzzle
          re-renders with a smaller geom (e.g. difficulty change from Expert
          size-4 → Easy size-3) before App clears winEpicenter, the old
          index would point past the new geom and crash. Bail in that case
          and let the next render with a cleared epicenter take over. */}
      {winEpicenter != null && geom[winEpicenter] != null && geom.map((g, i) => {
        const epi = geom[winEpicenter];
        const dx = g.x - epi.x;
        const dy = g.y - epi.y;
        const dist = Math.hypot(dx, dy);
        const delayMs = Math.round(dist * 4);
        return (
          <g key={`win-ripple-${i}`} transform={`translate(${g.x},${g.y})`}>
            <circle
              className="hm-win-ripple"
              cx={0} cy={0} r={R * 1.25}
              style={{ animationDelay: `${delayMs}ms` }}
            />
          </g>
        );
      })}
    </svg>
  );
}

export type { DragState };
